/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useCallback, useEffect } from "react";

// antd:
import { Typography, Form } from "antd";
import Prism from "prismjs";
// import { LoadingOutlined } from "@ant-design/icons";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

import DivE from "../react/DivE.tsx";
import SpanE from "../react/SpanE.tsx";
import BrE from "../react/BrE.tsx";
// Redux-hooks:
import { useAppSelector, useAppDispatch } from "../../libs/redux-hooks.ts";
// Reducers:
import { getLng, getDict, getTheme, getAppState, getUser, changeAppState, getSafePtr } from "../../libs/reducer.ts"; // getAppState
// My functions
import fnc from "../../libs/fnc.ts";
// Types:
import type {TuniversalElementProps} from "../../types/Iuigen.d.ts";
import type {Tpermissions} from "../../types/easyTypes.ts";

// const { Title, Text, Link, Paragraph } = Typography;

const { rz } = window;

type TexType = {
  blockType:"Text" | "Title" | "Paragraph",
  type?:"default" | "secondary" | "success" | "warning" | "danger"
  level?:number,
  size?:string,
  disabled?:boolean,
  mark?:boolean,
  code?:boolean,
  keyboard?:boolean,
  underline?:boolean,
  delete?:boolean,
  strong?:boolean,
  italic?:boolean,
  copyable?:boolean,
  editable?:boolean,
  ellipsis?:boolean | {tooltip?:string, rows?:number, expandable?:boolean, symbol?:string, suffix?:string},
  language?:"javascript" | "html" | "css" | "xml"
}

const Etext:FC<TuniversalElementProps> = ({attributes, children}) => {
  Etext.displayName = "Etext";
  const dispatch = useAppDispatch();
  const user = useAppSelector(getUser);
  const dct = useAppSelector(getDict);
  const lng = useAppSelector(getLng);
  const theme = useAppSelector(getTheme);
  const safe = useAppSelector(getSafePtr);
  const rlng = (lng === "en" ? "ru" : "en");

  const {rootAttributes, key, fullPath, type, align} = attributes; // onClick, onChange, onRightClick, onHover, onLoad
  const appState = useAppSelector(getAppState(rootAttributes.key)), elState = appState[fullPath]; // value, visible, disabled, loading, userStyle, userClass
  const {color, backgroundColor, format} = elState.attributes;
  const rootNamespace = useMemo(() => (window[rootAttributes?.key || rz.strMakeID(24)]), [rootAttributes?.key]);
  const isParentForm = useMemo(() => {
    const parentPath = rz.strParentFolderName(fullPath || ""), parentState = appState[parentPath];
    return Boolean(parentState.name === "form");
  }, [appState, fullPath]);

  const permissions = useMemo<Tpermissions>(() => (fnc.checkPermissions(user, attributes)), [user, attributes]);

  const ttype = useMemo<TexType>(() => {
    const typ:TexType = {blockType: "Text"};
    (type || format || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if (~["h1", "h2", "h3", "h4", "h5"].indexOf(tt)) { typ.level = rz.strSearchNum(tt, 1); typ.blockType = "Title" }
      else if (~["default", "secondary", "success", "warning", "danger"].indexOf(tt)) { typ.type = tt; typ.blockType = "Text" }
      else if (tt === "paragraph") { typ.blockType = "Paragraph" }
      else if (tt === "disabled") { typ.disabled = true }
      else if (tt === "mark") { typ.mark = true }
      else if (tt === "code") { typ.code = true }
      else if (tt === "keyboard") { typ.keyboard = true }
      else if (tt === "underline") { typ.underline = true }
      else if (tt === "delete") { typ.delete = true }
      else if (~["strong", "bold"].indexOf(tt)) { typ.strong = true }
      else if (tt === "italic") { typ.italic = true }
      else if (~["editable", "edit"].indexOf(tt)) { typ.editable = true }
      else if (~["copyable", "copy"].indexOf(tt)) { typ.copyable = true }
      // @ts-ignore
      else if (~["javascript", "html", "css", "xml"].indexOf(tt)) { typ.code = true; typ.language = tt }
      else if ((/\d+(\.\d+)?em$/ig).test(tt) || (/\d+(\.\d+)?rem$/ig).test(tt) || (/\d+(\.\d+)?px$/ig).test(tt) || (/\d+(\.\d+)?pt$/ig).test(tt)) { typ.size = tt }
    });

    if (attributes.onChange) { typ.editable = true }
    if (attributes.editable === "true" || attributes.edit === "true") { typ.editable = true }
    if (attributes.copyable === "true" || attributes.copy === "true") { typ.copyable = true }
    if (attributes.size) { typ.size = attributes.size }
    if (attributes.ellipsis) {
      if (attributes.ellipsis === "true") { typ.ellipsis = {expandable: true, rows: 2} }
      else if (attributes.ellipsis.startsWith("{") && attributes.ellipsis.endsWith("}")) {
        const jsnb = (rz.strReplaceAll(attributes.ellipsis, "'", "\"")).trim(); let jsn; try { jsn = JSON.parse(jsnb) } catch (err) { console.log(err) }
        if (jsn) { typ.ellipsis = jsn } else { typ.ellipsis = {expandable: true, rows: 2} }
      }
    }
    if (attributes.language) { typ.code = true; typ.language = attributes.language }
    return typ;
  }, [attributes.copy, attributes.copyable, attributes.edit, attributes.editable, attributes.ellipsis, attributes.language, attributes.onChange, attributes.size, format, type]);

  const clsu = useMemo<Array<string>>(() => {
    const userClss = rz.objClone(elState?.userClass || []);
    if ((elState?.disabled || permissions.disabled === true) && !~userClss.indexOf("uk-disabled")) { userClss.push("uk-disabled") }
    return userClss;
  }, [elState?.userClass, elState?.disabled, permissions]); // format

  const styl = useMemo<CSS.Properties>(() => {
    const est = {...elState.userStyle};
    if ((elState?.visible === false || permissions.visible === false) && est.display !== "none") { est.display = "none" }
    else if ((elState?.visible === true && permissions.visible !== false) && est.display === "none") { est.display = undefined }
    let clr:string; const allc = (color || "").split("|"); if (allc.length > 1) { clr = (theme === "dark" ? allc[1] : allc[0]) } else { clr = allc[0] }
    if (!est?.color && clr) { est.color = clr }
    let bclr:string; const allb = (backgroundColor || "").split("|"); if (allb.length > 1) { bclr = (theme === "dark" ? allb[1] : allb[0]) } else { bclr = allb[0] }
    if (!est?.backgroundColor && bclr) { est.backgroundColor = bclr }
    if (ttype.size) { est.fontSize = ttype.size }
    return est;
  }, [elState.userStyle, elState?.visible, permissions.visible, color, backgroundColor, ttype.size, theme]);

  const title = useMemo<string | undefined>(() => {
    const chld = (children && rz.isString(children[0]) && children[0]) || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
    let ttl = (elState?.value || elState.attributes["title_" + lng] || elState.attributes.title || elState.attributes["title_" + rlng] || attributes["title_" + lng] || attributes.title || attributes["title_" + rlng] || chld);
    if (!ttl) { return "" }
    if (rz.isString(ttl)) {
      while (ttl.startsWith("\n")) { ttl = ttl.slice(1) }
      while (ttl.endsWith("\n")) { ttl = ttl.slice(0, -1) }
      if (~ttl.indexOf("\n")) {
        const aa = ttl.split("\n");
        if (ttype.code) {
          ttl = React.createElement(DivE, {key: "dive-wrapper-" + (key || "")}, aa.map((ii, i) => {
            const bb = ii.replace((/^(\s+)/g), (cc) => (cc.replace((/(\s{2,2})/g), " ")));
            const html = Prism.highlight((bb || ""), Prism.languages.javascript, ttype.language || "javascript");
            return React.createElement(SpanE, {key: "span-wrapper-" + (i + 1) + "-" + (key || "")}, [
              // eslint-disable-next-line react/no-danger-with-children
              React.createElement(SpanE, {key: "span-content-" + (i + 1) + "-" + (key || ""), dangerouslySetInnerHTML: {__html: html}}),
              React.createElement(BrE, {key: "br-" + (i + 1) + "-" + (key || "")})
            ])
          }));
        }
        else {
          ttl = React.createElement(DivE, {key: "dive-wrapper-" + (key || "")}, aa.map((ii, i) => {
            const bb = ii.replace((/^(\s+)/g), (cc) => (cc.replace((/(\s{2,2})/g), " ")));
            return React.createElement(SpanE, {key: "span-wrapper-" + (i + 1) + "-" + (key || "")}, [
              React.createElement(SpanE, {key: "span-content-" + (i + 1) + "-" + (key || "")}, bb),
              React.createElement(BrE, {key: "br-" + (i + 1) + "-" + (key || "")})
            ])
          }));
        }
      }
    }
    return ttl;
  }, [children, elState?.value, elState.attributes, lng, rlng, attributes, ttype.code, ttype.language, key]);
  // eslint-disable-next-line react-hooks/exhaustive-deps, max-len
  const title2 = useMemo<string | undefined>(() => (elState?.attributes["title2_" + lng] || elState?.attributes.title2 || elState?.attributes["title2_" + rlng] || attributes["title2_" + lng] || attributes.title2 || attributes["title2_" + rlng]), [elState?.attributes, attributes, lng]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const tooltip = useMemo<string | undefined>(() => (elState.attributes["tooltip_" + lng] || elState.attributes.tooltip || elState.attributes["tooltip_" + rlng]), [elState.attributes, lng]);

  const onChange = useCallback((newText) => {
    if (fnc.config?.isDev) console.log({action: "<Text key='" + key + "'> -> onChange()", value: newText});
    dispatch(changeAppState({appkey: rootAttributes.key, fullPath, newState: {...elState, value: newText}}));
    if (rootNamespace && (attributes.onChange || attributes.onchange) && rootNamespace[attributes.onChange || attributes.onchange]) {
      const clientFNC = rootNamespace[attributes.onChange || attributes.onchange];
      if (rz.isPromise(clientFNC)) {
        clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: newText, safe}).then((ret) => {
          if (rz.isUndefinedOrNull(ret)) { return }
          if (ret && ret.err) { console.log(ret.err) }
          if (ret && ret?.state?.fullPath && ret?.state?.newState) {
            dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
          }
        });
      }
      else if (rz.isFunction(clientFNC)) {
        const ret = clientFNC({appkey: rootAttributes.key, attributes, key, user, lng, dct, value: newText, safe}); if (rz.isUndefinedOrNull(ret)) { return }
        if (ret && ret.err) { console.log(ret.err) }
        if (ret && ret?.state?.fullPath && ret?.state?.newState) {
          dispatch(changeAppState({appkey: rootAttributes.key, fullPath: ret.state.fullPath, newState: ret.state.newState}));
        }
      }
    }
  }, [key, dispatch, rootAttributes.key, fullPath, elState, rootNamespace, attributes, user, lng, dct, safe]);

  const flex = useMemo<string>(() => {
    const flx = ["uk-flex"];
    (align || "").toLowerCase().replace((/(\s+|_|\|)/igm), " ").split(" ").forEach((tt) => {
      if ((/^(left|center|right|between|around|stretch|top|middle|bottom|row|column|wrap|nowrap|first|last|none|auto|\d+)/igm).test(tt)) { flx.push("uk-flex-" + tt) }
    });
    return flx.join(" ");
  }, [align]);

  const cProps = useMemo(() => {
    const prps:{[key:string]:any} = { style: styl, className: clsu.join(" ") };

    if (ttype.level) { prps.level = ttype.level }
    if (ttype.type) { prps.type = ttype.type }
    if (!rz.isUndefinedOrNull(ttype.mark)) { prps.mark = ttype.mark }
    if (!rz.isUndefinedOrNull(ttype.code)) { prps.code = ttype.code }
    if (!rz.isUndefinedOrNull(ttype.keyboard)) { prps.keyboard = ttype.keyboard }
    if (!rz.isUndefinedOrNull(ttype.underline)) { prps.underline = ttype.underline }
    if (!rz.isUndefinedOrNull(ttype.strong)) { prps.strong = ttype.strong }
    if (!rz.isUndefinedOrNull(ttype.italic)) { prps.italic = ttype.italic }
    if (!rz.isUndefinedOrNull(ttype.delete)) { prps.delete = ttype.delete }
    if (!rz.isUndefinedOrNull(ttype.copyable)) { prps.copyable = ttype.copyable }
    if (!rz.isUndefinedOrNull(ttype.ellipsis)) { prps.ellipsis = ttype.ellipsis }

    if (tooltip) { prps["uk-tooltip"] = tooltip }
    if (ttype.disabled || elState.disabled || permissions.disabled === true) { prps.disabled = true }
    if (elState?.visible === false || permissions.visible === false) { prps.hidden = true }
    if (ttype.editable && (!permissions.disabled && (permissions.editable !== false))) { prps.editable = {onChange} }

    return prps;
  }, [clsu, elState.disabled, elState?.visible, onChange, permissions.disabled, permissions.editable, permissions.visible, styl, tooltip, ttype]);

  const wrapperForm = useCallback((mainComponent:React.ReactNode) => {
    if (isParentForm) {
      const frmItemProps:{[key:string]:any} = {
        key: key + "-formitem", label: (<span className="rz-txt-std">{title2}</span>), className: "rz-width-expand",
        style: ((elState?.visible === false || permissions.visible === false) ? {display: "none"} : undefined)
      };
      // eslint-disable-next-line max-len
      return (
        <Form.Item {...frmItemProps}>
          {mainComponent}
        </Form.Item>
      )
    }
    return mainComponent;
  }, [isParentForm, title2, key, elState?.visible, permissions.visible]);

  const TypographyElement = useMemo(() => {
    if (ttype.code && !rz.isString(title)) {
      return (
        <Typography.Text key={key} id={key} code={true} >{title}</Typography.Text>
      );
    }
    if (ttype.blockType === "Title") {
      return (
        <Typography.Title key={key} id={key} {...cProps} >{title}</Typography.Title>
      );
    }
    if (ttype.blockType === "Paragraph") {
      return (
        <Typography.Paragraph key={key} id={key} {...cProps} >{title}</Typography.Paragraph>
      );
    }
    return (
      <Typography.Text key={key} id={key} {...cProps} >{title}</Typography.Text>
    );
  }, [cProps, key, title, ttype.blockType, ttype.code]);

  if (!align) { return wrapperForm(TypographyElement) }

  return (wrapperForm(<DivE key={key + "-typography-align-wrapper"} className={flex}>{TypographyElement}</DivE>));
}

export default Etext;
/* eslint-enable @typescript-eslint/no-unused-vars */
